'use client';

import { type JSX, useCallback, useEffect, useRef } from 'react';

import { type Suggestion } from './TextAreaInputField';

export function SuggestionRow({
    suggestions,
    disabled,
    onSuggestionClick,
}: {
    suggestions: Suggestion[];
    disabled?: boolean;
    onSuggestionClick: (suggestion: Suggestion) => void;
}): JSX.Element {
    const scrollRef = useRef<HTMLDivElement>(null);
    const rightFadeRef = useRef<HTMLDivElement>(null);
    const leftFadeRef = useRef<HTMLDivElement>(null);

    const updateFade = useCallback((): void => {
        const el = scrollRef.current;
        if (!el) return;
        const remaining = el.scrollWidth - el.scrollLeft - el.clientWidth;
        if (rightFadeRef.current) {
            rightFadeRef.current.style.opacity = String(Math.min(remaining / 40, 1));
        }
        if (leftFadeRef.current) {
            leftFadeRef.current.style.opacity = String(Math.min(el.scrollLeft / 40, 1));
        }
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const observer = new ResizeObserver(updateFade);
        observer.observe(el);
        updateFade();
        return () => observer.disconnect();
    }, [updateFade]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const onWheel = (e: WheelEvent): void => {
            if (e.deltaY === 0) return;
            if (el.scrollWidth <= el.clientWidth) return;
            e.preventDefault();
            el.scrollLeft += e.deltaY;
        };

        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    return (
        <div className="relative px-2 pt-2 pb-4 -mb-2 rounded-t-md overflow-clip dark:bg-neutral-dark-200">
            <div ref={scrollRef} onScroll={updateFade} className="flex gap-1 overflow-x-auto scrollbar-hidden">
                {suggestions.map((suggestion) => (
                    <button
                        key={suggestion.label}
                        type="button"
                        disabled={disabled}
                        onClick={() => onSuggestionClick(suggestion)}
                        className="shrink-0 rounded-full px-3 py-1 text-xs whitespace-nowrap cursor-pointer dark:bg-neutral-dark-300 dark:text-neutral-dark-1000 dark:hover:border-neutral-dark-700 border border-transparent disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {suggestion.label}
                    </button>
                ))}
            </div>
            <div
                ref={leftFadeRef}
                className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 bg-linear-to-r from-neutral-dark-200 to-transparent"
            />
            <div
                ref={rightFadeRef}
                className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-linear-to-l from-neutral-dark-200 to-transparent"
            />
        </div>
    );
}
